<template>
    <div class="payment__status expired" v-if="payment">
        <template v-if="payment.account">
            <i class="icon status-icon"></i>
            <PaymentStatusText
                :title="$t('light-payment.status.expired.title')"
                :text="$t('light-payment.status.expired.text')"
                :advice="$t('light-payment.status.expired.advice')"
            />
        </template>
        <template v-else>
            <template
                v-if="payment.subtype && [PurchaseOperationSubtype.default, PurchaseOperationSubtype.lite].includes(payment.subtype)">
                <SlothBlock
                    :title="$t('payment.statuses.expired.no-agent.title')"
                    :subtitle="$t('payment.statuses.expired.no-agent.subtitle')"
                    :type="SLOTH_TYPE.QUESTION"
                ></SlothBlock>
<!--                <f7-button-->
<!--                    :disabled="actionBtnLoading"-->
<!--                    :loading="actionBtnLoading"-->
<!--                    preloader-->
<!--                    class="theme-type"-->
<!--                    @click="submit"-->
<!--                >-->
<!--                    {{ $t('payment.statuses.expired.no-agent.actions.recreate') }}-->
<!--                </f7-button>-->
				<f7-button
					preloader
					class="theme-type"
					@click="toPaymentsHandler"
				>
					{{ $t('payment.statuses.expired.no-agent.actions.back') }}
				</f7-button>
            </template>
        </template>
    </div>
</template>

<script setup lang="ts">
import {computed, ref, toRef, watch} from "vue";
import PurchaseOperation from "@models/operations/PurchaseOperation";
import {ReactiveBuyingOperation} from "@/services/operations/AbstractBuyingService";
import {SLOTH_TYPE} from "@/entities/enums/SlothType";
import SlothBlock from "@/components/messages/SlothBlock.vue";
import PaymentStatusText from "../atomic/PaymentStatusText.vue";
import {PurchaseOperationSubtype} from "@enums/PurchaseOperationSubtype";

interface IProps {
    payment: PurchaseOperation,
    timeText?: string,
    percent?: number,
}

enum TIMER_STATUS_TYPE {
    DEFAULT = "DEFAULT",
    WARNING = "WARNING",
    ALERT = "ALERT"
}

const setTimerColors = {
    DEFAULT: "#1890FF",
    WARNING: "#E69700",
    ALERT: "#FF3B30"
};

const props = defineProps<IProps>();
const emits = defineEmits(['submit', 'toPayments']);
const payment: ReactiveBuyingOperation = toRef(props, 'payment');
const requisites = computed(() => payment.value?.requisites);
const percent = toRef(props, 'percent');
const timeText = toRef(props, 'timeText');
const buyingIsAvailable = ref(true);
const timerBorderColor = ref(setTimerColors[TIMER_STATUS_TYPE.DEFAULT]);
const timerTextColor = ref(setTimerColors[TIMER_STATUS_TYPE.DEFAULT]);
const actionBtnLoading = ref(false);

const submit = () => {
    actionBtnLoading.value = true;
    emits('submit', () => {
        actionBtnLoading.value = false;
    });
}

const toPaymentsHandler = () => {
	emits('toPayments');
}

if (timeText.value) {
    detectBuyingIsAvailable(timeText.value);
}

watch(timeText, (value) => {
    if (value) {
        detectBuyingIsAvailable(value);
    }
});

function detectBuyingIsAvailable(value: string) {
    const minute = +value.split(':')[0];
    if (minute == 4) {
        timerBorderColor.value = setTimerColors[TIMER_STATUS_TYPE.WARNING];
        timerTextColor.value = setTimerColors[TIMER_STATUS_TYPE.WARNING];
    }
    if (minute == 1 || minute == 0) {
        timerBorderColor.value = setTimerColors[TIMER_STATUS_TYPE.ALERT];
        timerTextColor.value = setTimerColors[TIMER_STATUS_TYPE.ALERT];
    }
    if (minute <= 0) {
        buyingIsAvailable.value = false;
    }
}

</script>

<style src="./index.scss"></style>
<style lang="scss">
.payment__status {
    &.expired {
        display: flex;
        align-items: center;
        flex-direction: column;
        padding: 56px 16px 16px 0px;

        .button {
            padding: 0 32px;
        }
    }

    .status-icon {
        margin-bottom: 32px;
        width: 100px;
        height: 100px;
        background-image: url('data:image/svg+xml,<svg width="101" height="100" viewBox="0 0 101 100" fill="none" xmlns="http://www.w3.org/2000/svg"><g clip-path="url(%23clip0_9906_37893)"><path d="M50.3242 43.3779L59.6797 34.1787C61.5156 32.3623 64.4844 32.3623 66.1445 34.1787C68.1367 36.0146 68.1367 38.9834 66.1445 40.6436L57.1211 49.8232L66.1445 59.1787C68.1367 61.0147 68.1367 63.9834 66.1445 65.6436C64.4844 67.6357 61.5156 67.6357 59.6797 65.6436L50.3242 56.6201L41.1445 65.6436C39.4844 67.6357 36.5156 67.6357 34.6797 65.6436C32.8633 63.9834 32.8633 61.0147 34.6797 59.1787L43.8789 49.8232L34.6797 40.6436C32.8633 38.9834 32.8633 36.0146 34.6797 34.1787C36.5156 32.3623 39.4844 32.3623 41.1445 34.1787L50.3242 43.3779Z" fill="%23FF3B30"/><path opacity="0.4" d="M0.5 50C0.5 22.3828 22.8828 0 50.5 0C78.1172 0 100.5 22.3828 100.5 50C100.5 77.6172 78.1172 100 50.5 100C22.8828 100 0.5 77.6172 0.5 50ZM34.6797 40.6445L43.8789 49.8242L34.6797 59.1797C32.8633 61.0156 32.8633 63.9844 34.6797 65.6445C36.5156 67.6367 39.4844 67.6367 41.1445 65.6445L50.3242 56.6211L59.6797 65.6445C61.5156 67.6367 64.4844 67.6367 66.1445 65.6445C68.1367 63.9844 68.1367 61.0156 66.1445 59.1797L57.1211 49.8242L66.1445 40.6445C68.1367 38.9844 68.1367 36.0156 66.1445 34.1797C64.4844 32.3633 61.5156 32.3633 59.6797 34.1797L50.3242 43.3789L41.1445 34.1797C39.4844 32.3633 36.5156 32.3633 34.6797 34.1797C32.8633 36.0156 32.8633 38.9844 34.6797 40.6445Z" fill="%23FF3B30"/></g><defs><clipPath id="clip0_9906_37893"><rect width="100" height="100" fill="white" transform="translate(0.5)"/></clipPath></defs></svg>');
    }
}
</style>
